import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {BurgerMenu} from './BurgerMenu';
import {SubMenu} from './SubMenu';
import {MenuSections} from './types';
import {LinkPropType, Link} from '../../Link';
import {useLocalizedUrl} from '../../../hooks/useLocalizedUrl';
import {Button} from '../../Button';
import * as Logo from '../../icon/Logo';
import classNames from 'classnames';
import {localeToPath} from '../../../utils/localeToPath';
import type {LocaleEnum} from '../../../types/locale';
import styled from 'styled-components';
import {useGetNavItems} from '../../../hooks/useGetNavItems';

type Props = {
  links: MenuSections;
  currentPathname: string;
};

// Tailwind does not allow dynamic arbitrary colors
// https://tailwindcss.com/docs/content-configuration#dynamic-class-names
export const DynamicColorLi = styled.li<{bg: string; textColor: string; actionTextColor: string}>`
  & > a {
    background: ${props => props.bg ?? 'initial'};
    color: ${props => props.textColor ?? 'initial'};
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    &:hover,
    &:focus,
    &:active {
      background: ${props => (props.bg ? `${props.bg}99` : 'initial')};
    }
    :hover > a,
    :focus > a {
      color: ${props => (props.actionTextColor ? `${props.actionTextColor} !important` : 'initial')};
    }
  }
`;

export const Menu: React.FC<Props> = props => {
  const localizeUrl = useLocalizedUrl();
  const entries = props.links.other;
  const intl = useIntl();
  const navigation = useGetNavItems(intl);

  const commonLinkClasses =
    'font-body text-base tracking-tight relative h-full flex items-center transition-all ease-in-out duration-300 cursor-pointer border-none outline-none';
  const textLinkClasses = 'text-black hover:text-primary focus:text-primary';

  return (
    <div className='flex h-70px justify-between'>
      <nav className='h-full flex'>
        <Link to={props.links.logoUrl} className='h-full inline-block flex items-center' dataTestId='navbar-logo'>
          <div className='w-auto h-35px'>
            <Logo.Big />
          </div>
        </Link>

        <ul className='hidden xl:inline-block m-0 font-body'>
          {navigation.map((navigationItem, index) => (
            <DynamicColorLi
              className='ml-1 h-full inline-block'
              key={`${navigationItem.title}-${index}`}
              bg={navigationItem.bg ? `${navigationItem.bg.hex}` : null}
              textColor={navigationItem.textColor ? `${navigationItem.textColor.hex}` : null}
              actionTextColor={navigationItem.actionTextColor ? `${navigationItem.actionTextColor.hex}` : null}
            >
              {navigationItem.submenu.length > 0 && (
                <SubMenu
                  dataTestId={`${navigationItem.title}`}
                  linkClasses={commonLinkClasses}
                  overrideTextColor={navigationItem.bg ? '#4A4A4A' : null}
                  {...navigationItem}
                  bg={navigationItem.bg ? `${navigationItem.bg.hex}` : null}
                  textColor={navigationItem.textColor ? `${navigationItem.textColor.hex}` : null}
                  actionTextColor={navigationItem.actionTextColor ? `${navigationItem.actionTextColor.hex}` : '#1493BE'}
                  locale={intl.locale as LocaleEnum}
                />
              )}
              {navigationItem.submenu.length === 0 && (
                <Link
                  className={classNames(commonLinkClasses, {
                    [textLinkClasses]: navigationItem.bg === null
                  })}
                  to={`/${localeToPath(intl.locale as LocaleEnum)}/${navigationItem.slug?.slug}/`}
                >
                  {navigationItem.title}
                </Link>
              )}
            </DynamicColorLi>
          ))}
        </ul>
      </nav>

      <nav className='h-full flex items-center'>
        <ul className='hidden md:inline-block h-full m-0 font-body'>
          {entries.map((entry, index) => {
            const targetProps = entry.targetBlank ? {target: '_blank' as LinkPropType['target'], rel: 'noopener noreferrer'} : {};
            return (
              <li className='pr-5 h-full inline-block' key={`${entry.href}-${index}`}>
                <Link className={classNames(commonLinkClasses, textLinkClasses)} to={entry.href} dataTestId={entry.dataTestId} {...targetProps}>
                  {entry.label}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className='hidden sm:inline-block flex items-center' id='hmbm_account'>
          <Button sm={true} noFloat={true} to={localizeUrl('/signup/start')} dataTestId='nav-signup'>
            <FormattedMessage id='header.testbutton' />
          </Button>
        </div>

        <div className='xl:hidden'>
          <BurgerMenu links={props.links} currentPathname={props.currentPathname} />
        </div>
      </nav>
    </div>
  );
};
